import React, { useRef, useEffect, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Mousewheel } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import testimonialsData from '../../data/testimonialsData';
import { typography, layout, colors } from '../../styles/designTokens';
import clipperHeadBlades from '../../assets/clipper-head-1.png'; // תמונת הלהבים (החלק העליון)
import clipperHeadBase from '../../assets/clipper-head-2.png'; // תמונת החלק המשובץ (המסגרת/רקע)
import ProductCard from '../ProductCard';

const TestimonialsSection: React.FC = () => {
  // כל הכרטיסים
  const allTestimonials = useMemo(() => testimonialsData, []);
  
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const slideCardCacheRef = useRef<Map<HTMLElement, HTMLElement>>(new Map());
  const lastSuctionStateRef = useRef<Map<number, { isClosest: boolean; distanceLevel: string | null; isFading: boolean; isHidden: boolean }>>(new Map());
  const baseEntryTimeRef = useRef<Map<number, number>>(new Map());
  const approachSmoothingRef = useRef<Map<number, number>>(new Map());
  const extraShrinkSmoothRef = useRef<Map<number, number>>(new Map());
  const blurBoostSmoothRef = useRef<Map<number, number>>(new Map());
  const activeCardIdRef = useRef<number | null>(null);
  const lockedCardIdRef = useRef<number | null>(null); // כרטיס שנעול בשאיבה
  const sectionRef = useRef<HTMLElement>(null);
  const sectionVisibleRef = useRef(true);
  const prefersReducedMotionRef = useRef(false);
  const [isVisible, setIsVisible] = React.useState(true);
  const [hoveredCardId, setHoveredCardId] = React.useState<number | null>(null);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const swiperRef = useRef<SwiperType | null>(null);
  const clipperContainerRef = useRef<HTMLDivElement>(null);
  const clipperBodyRef = useRef<HTMLDivElement>(null);
  const clipperBladesImageRef = useRef<HTMLImageElement>(null);
  const clipperBaseImageRef = useRef<HTMLImageElement>(null);
  const previousIndexRef = useRef<number>(0);
  
  // Cache למיקום המכונה - נבדוק פעם אחת במקום 15 פעמים בשנייה!
  const cachedMachinePositionRef = useRef<{
    clipperRect: DOMRect;
    bodyRect: DOMRect | null;
    bladesRect: DOMRect | null;
    baseRect: DOMRect | null;
    targetX: number;
    targetY: number;
  } | null>(null);

  // עצירת/המשכת הגלילה האוטומטית בהתאם ל-hover
  useEffect(() => {
    if (swiperRef.current) {
      if (hoveredCardId !== null) {
        swiperRef.current.autoplay?.stop();
      } else {
        swiperRef.current.autoplay?.start();
      }
    }
  }, [hoveredCardId, swiperRef]);

  // עצירת/המשכת הגלילה האוטומטית בהתאם לנראות במסך
  useEffect(() => {
    if (swiperRef.current) {
      if (isVisible) {
        swiperRef.current.autoplay?.start();
      } else {
        swiperRef.current.autoplay?.stop();
      }
    }
  }, [isVisible]);

  // מעקב נראות הסקשן כדי לחסוך חישובים כשהוא מחוץ למסך
  useEffect(() => {
    const target = sectionRef.current;
    if (!target || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting;
        sectionVisibleRef.current = visible;
        setIsVisible(visible);
      },
      { threshold: 0.05 }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, []);

  // תמיכה ב-prefers-reduced-motion
  useEffect(() => {
    if (typeof window === 'undefined' || !('matchMedia' in window)) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)') as MediaQueryList & {
      addListener?: (listener: (event: MediaQueryListEvent) => void) => void;
      removeListener?: (listener: (event: MediaQueryListEvent) => void) => void;
    };
    prefersReducedMotionRef.current = mediaQuery.matches;

    const handleChange = () => {
      prefersReducedMotionRef.current = mediaQuery.matches;
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', handleChange);
      } else if (typeof mediaQuery.removeListener === 'function') {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // אפקט שאיבה מקצועי - מתעדכן בכל פריים כדי להבטיח תנועה חלקה
  // Throttle: 15 FPS במקום 60 FPS - חיסכון של 75%!
  useEffect(() => {
    let animationFrameId: number | null = null;
    let lastUpdateTime = 0;
    const throttleDelay = 1000 / 15; // 15 FPS - מספיק לתנועה חלקה

    const updateSuctionEffect = (currentTime: number) => {
      // 🚀 PERFORMANCE FIX: עצירת האנימציה לחלוטין כשלא צריך - חיסכון של 90% במשאבים!
      
      // כיבוי אפקט השאיבה במובייל וטאבלט (עד 1024px)
      if (window.innerWidth <= 1024) {
        return; // ✅ לא קוראים ל-requestAnimationFrame - האנימציה נעצרת לגמרי!
      }

      // עצירת אפקט השאיבה כשיש hover על כרטיס - הקרוסלה עצרה אז גם השאיבה תעצור
      if (hoveredCardId !== null) {
        return; // ✅ לא קוראים ל-requestAnimationFrame - האנימציה נעצרת לגמרי!
      }

      // עצירה כשלא במסך - חיסכון באנרגיה
      if (!isVisible) {
        return; // ✅ לא קוראים ל-requestAnimationFrame - האנימציה נעצרת לגמרי!
      }

      if (!sectionVisibleRef.current || prefersReducedMotionRef.current) {
        return; // ✅ לא קוראים ל-requestAnimationFrame - האנימציה נעצרת לגמרי!
      }

      // Throttle - רק כל 66ms (15 פעמים בשנייה)
      if (currentTime - lastUpdateTime < throttleDelay) {
        animationFrameId = requestAnimationFrame(updateSuctionEffect);
        return;
      }
      lastUpdateTime = currentTime;

      const swiper = swiperRef.current;
      const clipperContainer = clipperContainerRef.current;
      const clipperBody = clipperBodyRef.current;
      const bladesImage = clipperBladesImageRef.current;
      const baseImage = clipperBaseImageRef.current;

      if (swiper && clipperContainer) {
        // שימוש ב-cache - בודקים את מיקום המכונה רק אם אין cache או אם המסך השתנה
        if (!cachedMachinePositionRef.current) {
          const clipperRect = clipperContainer.getBoundingClientRect();
          const bodyRect = clipperBody?.getBoundingClientRect() || null;
          const bladesRect = bladesImage?.getBoundingClientRect() || null;
          const baseRect = baseImage?.getBoundingClientRect() || bodyRect;
          const targetX = baseRect ? baseRect.left + baseRect.width * 0.35 : clipperRect.left + clipperRect.width * 0.25;
          const targetY = baseRect ? baseRect.top + baseRect.height * 0.5 : clipperRect.top + clipperRect.height * 0.5;
          
          cachedMachinePositionRef.current = {
            clipperRect,
            bodyRect,
            bladesRect,
            baseRect,
            targetX,
            targetY,
          };
        }
        
        // שימוש במיקום השמור
        const { clipperRect, bodyRect, bladesRect, baseRect, targetX, targetY } = cachedMachinePositionRef.current;
        const suctionZone = 480;
        const nearThreshold = suctionZone * 1.2;
        const closeThreshold = suctionZone * 0.9;
        const veryCloseThreshold = suctionZone * 0.6;
        const extremeCloseThreshold = suctionZone * 0.35;

        const slideMeta: Array<{
          card: HTMLElement;
          distance: number;
          dx: number;
          dy: number;
          rect: DOMRect;
        }> = [];

        swiper.slides.forEach((slideEl: HTMLElement) => {
          let card = slideCardCacheRef.current.get(slideEl);
          if (!card) {
            const found = slideEl.querySelector('.testimonial-card-modern') as HTMLElement | null;
            if (found) {
              card = found;
              slideCardCacheRef.current.set(slideEl, found);
            }
          }
          if (!card) return;

          const cardRect = card.getBoundingClientRect();
          // משתמשים בקצה הימני של הכרטיס כדי למדוד "כניסה" למכונה
          const cardX = cardRect.right;
          const cardY = cardRect.top + cardRect.height / 2;
          const dx = targetX - cardX;
          const dy = targetY - cardY;
          const distance = Math.hypot(dx, dy);

          slideMeta.push({ card, distance, dx, dy, rect: cardRect });
        });

        if (slideMeta.length > 0) {
          const swiperRect = (swiper.el as HTMLElement).getBoundingClientRect();
          const visibleSlides = slideMeta.filter(({ rect }) =>
            rect.right > swiperRect.left && rect.left < swiperRect.right
          );
          const candidates = visibleSlides.length > 0 ? visibleSlides : slideMeta;
          const candidate = candidates.reduce((max, current) =>
            current.rect.right > max.rect.right ? current : max
          );
          const candidateId = Number(candidate.card.dataset.cardId || 0);
          const previousId = activeCardIdRef.current;
          let activeCard = candidate;

          // אם יש כרטיס נעול בשאיבה, נמשיך איתו
          // if (lockedCardIdRef.current !== null) {
          //   const lockedMeta = candidates.find(
          //     (entry) => Number(entry.card.dataset.cardId || 0) === lockedCardIdRef.current
          //   );
          //   if (lockedMeta) {
          //     activeCard = lockedMeta;
          //   } else {
          //     // הכרטיס הנעול נעלם - משחררים את הנעילה
          //     lockedCardIdRef.current = null;
          //   }
          // } else if (previousId && previousId !== candidateId) {
          //   const previousMeta = candidates.find(
          //     (entry) => Number(entry.card.dataset.cardId || 0) === previousId
          //   );
          //   if (previousMeta) {
          //     // threshold גדול יותר ליציבות - נשמור על הכרטיס הקודם כל עוד הוא בתוך/קרוב לגוף המכונה
          //     const keepPrevious = baseRect
          //       // ? previousMeta.rect.right <= baseRect.right + 80
          //       ? previousMeta.rect.right <= baseRect.right + 20
          //       : candidate.rect.right - previousMeta.rect.right < 50;
          //     if (keepPrevious) {
          //       activeCard = previousMeta;
          //     } else {
          //       activeCardIdRef.current = candidateId;
          //     }
          //   } else {
          //     activeCardIdRef.current = candidateId;
          //   }
          // } else {
            activeCardIdRef.current = candidateId;
          // }

          const entryRange = baseRect ? Math.max(220, baseRect.width * 0.9) : suctionZone;
          const entryDistance = baseRect ? baseRect.left - activeCard.rect.right : activeCard.distance;
          const approach = Math.min(1, Math.max(0, 1 - entryDistance / entryRange));

          const suctionActive = approach > 0.05;
          clipperContainer.classList.toggle('suction-active', suctionActive);
          if (clipperBody) {
            clipperBody.classList.toggle('suction-active', suctionActive);
          }

          slideMeta.forEach(({ card, distance, dx, dy, rect }) => {
            const isActiveCard = card === activeCard.card;
            let distanceLevel: string | null = null;
            if (isActiveCard) {
              if (approach > 0.85 || distance < extremeCloseThreshold) distanceLevel = 'extreme-close';
              else if (approach > 0.65 || distance < veryCloseThreshold) distanceLevel = 'very-close';
              else if (approach > 0.45 || distance < closeThreshold) distanceLevel = 'close';
              else if (approach > 0.2 || distance < nearThreshold) distanceLevel = 'near';
            }

            const isNearMachine = distanceLevel !== null;
            const isFading = false;
            const isClosest = isActiveCard && approach > 0;
            const insideBase =
              isActiveCard && baseRect
                ? rect.right >= baseRect.left &&
                  rect.right <= baseRect.right &&
                  rect.top + rect.height / 2 >= baseRect.top &&
                  rect.top + rect.height / 2 <= baseRect.bottom
                : false;
            const cardId = Number(card.dataset.cardId || 0);
            const lastState = lastSuctionStateRef.current.get(cardId);
            if (!isActiveCard) {
              approachSmoothingRef.current.delete(cardId);
              extraShrinkSmoothRef.current.delete(cardId);
              blurBoostSmoothRef.current.delete(cardId);
            }

            if (isNearMachine) {
              card.classList.add('near-machine');
              card.setAttribute('data-distance', distanceLevel || '');
            } else {
              card.classList.remove('near-machine');
              card.removeAttribute('data-distance');
            }


            if (isClosest) {
              card.classList.add('suction-trail');
              
              // נועל את הכרטיס ברגע שהוא מתחיל להישאב (approach > 0.3)
              // if (approach > 0.3 && lockedCardIdRef.current === null) {
              // if (approach > 0.5 && lockedCardIdRef.current === null) { 
            //  if (approach > 0.7 && lockedCardIdRef.current === null) {
            //   lockedCardIdRef.current = cardId;
            //   }
              
              // ללא smoothing - תגובה מיידית לשאיבה
              // תאוצה לא ליניארית כדי שהשאיבה תרגיש חזקה יותר לקראת הסוף
              // const suctionProgress = Math.min(1, Math.max(0, Math.pow(approach, 1.1)));
              const suctionProgress = approach > 0.6 ? Math.min(1, Math.max(0, Math.pow((approach - 0.6) / 0.4, 1.1))) : 0;

              // הגדלת המרחק כדי שהכרטיס יגיע עד עמוק בתוך גוף המכונה
              const trackBoost = baseRect ? baseRect.width * 1.4 + 60 : 450;
              const translateX = trackBoost * suctionProgress;
              const translateY = 0;
              // התחלה עדינה: מעט מתכווץ ונמתח כבר בתחילת השאיבה
              const baseShrink = 0.02;
              
              // ללא החלקה - תגובה מיידית
              const extraShrink = insideBase ? 0.15 : 0;
              
              // הקטנה והתכווצות - מהירה יותר כי הכרטיסים רצים מהר
              // suctionProgress מתחיל מ-0 (רחוק) עד 1 (קרוב מאוד)
              // נשתמש ב-suctionProgress^1.1 לאפקט מהיר יותר
              const delayedProgress = Math.pow(suctionProgress, 1.1);
              
              const shrinkFactor = 0.55;
              const scaleY = 1 - baseShrink - extraShrink - delayedProgress * shrinkFactor;
              
              // התכווצות מהצד - compression חזק יותר (מ-0.16 ל-0.35)
              const rightPinch = 1 - delayedProgress * 0.35;
              
              // compression נוסף מהצדדים - הכרטיס נדחס רוחבית
              const sideCompression = 1 - delayedProgress * 0.2;
              
              const scaleX = (1 - baseShrink - extraShrink - delayedProgress * shrinkFactor) * rightPinch * sideCompression;
              const stretchX = 1;
              const skewX = 0;
              const rotateZ = 0;
              
              // חישוב opacity בהדרגה - יורד רק בשלב מאוחר יותר
              let opacity = 1 - delayedProgress * 0.5;
              
              const finalBoost = Math.max(0, suctionZone - distance) < 60 ? 1.7 : 1;
              
              // ללא החלקה - תגובה מיידית
              const blurBoost = insideBase ? 2.2 : 0;
              
              // הטשטוש מתחיל עדין ומתגבר רק כשהכרטיס עמוק בפנים
              const blur = Math.min(9, (0.2 + delayedProgress) * 4.5 * finalBoost + blurBoost);
              
              // אפקט התפוגגות עם זוהר והבזק - הכרטיס הופך זהוב מזהיר לפני שנעלם
              const glowIntensity = Math.max(0, (delayedProgress - 0.6) * 2.5); // מתחיל ב-60% מהדרך
              
              // הבזק דרמטי ברגע האחרון (90%-100%)
              const flashIntensity = delayedProgress > 0.9 ? Math.sin((delayedProgress - 0.9) * 10 * Math.PI) * 2 : 0;
              
              const brightness = 1 + glowIntensity * 1.2 + flashIntensity; // עד 2.2 + הבזק
              const saturate = 1 + glowIntensity * 2 + flashIntensity * 0.5; // עד 3 - צבעים עזים מאוד
              const hueRotate = glowIntensity * 25; // סיבוב צבע לכיוון זהוב

              card.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) rotateZ(${rotateZ}deg) scaleX(${scaleX * stretchX}) scaleY(${scaleY}) skewX(${skewX}deg)`;
              card.style.transformOrigin = 'right center';
              card.style.opacity = String(Math.max(0, opacity));
              card.style.filter = `blur(${blur}px) brightness(${brightness}) saturate(${saturate}) hue-rotate(${hueRotate}deg)`;
              card.style.zIndex = '1400'; // מתחת לגוף המכונה (1600) כדי שהכרטיס יעבור מתחתיו
              card.style.willChange = 'transform, opacity, filter';
              
              // משחרר את הנעילה רק כשהכרטיס כמעט נעלם לגמרי
              if (opacity < 0.1 && lockedCardIdRef.current === cardId) {
                lockedCardIdRef.current = null;
              }
            } else if (lastState?.isClosest && lockedCardIdRef.current !== cardId) {
              // רק אם הכרטיס לא נעול - מאפס את הסגנונות
              card.classList.remove('suction-trail');
              // הוספת transition חלק לפני איפוס הסגנונות
              // card.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out, filter 0.3s ease-out';
             card.style.transition = 'transform 0.1s ease-out, opacity 0.1s ease-out, filter 0.1s ease-out';
              card.style.transform = '';
              card.style.opacity = '';
              card.style.filter = '';
              card.style.transformOrigin = '';
              card.style.willChange = '';
              // מחזיר את הכרטיס למצב רגיל מתחת לגוף המכונה
              card.style.zIndex = '400';
              
              // ניקוי ה-transition אחרי שהאנימציה הסתיימה
              // setTimeout(() => {
              //   if (card.style.transition) {
              //     card.style.transition = '';
              //   }
              // }, 300);
              setTimeout(() => {
                if (card.style.transition) {
                  card.style.transition = '';
                }
              }, 100);

            } else {
              card.classList.remove('suction-trail');
              // כל שאר הכרטיסים נשארים מתחת לגוף המכונה
              card.style.zIndex = '400';
            }

            if (baseRect && isActiveCard) {
              const centerX = rect.left + rect.width / 2;
              const centerY = rect.top + rect.height / 2;
              const betweenImages = bladesRect
                ? centerX >= bladesRect.left &&
                  centerX <= bladesRect.right &&
                  centerY >= bladesRect.top &&
                  centerY <= bladesRect.bottom
                : insideBase;

              const hideThresholdX = baseRect.left + baseRect.width * 0.9;
              const shouldHide = betweenImages && rect.right >= hideThresholdX;

              const now = performance.now();
              const entryKey = cardId;
              if (insideBase && !baseEntryTimeRef.current.has(entryKey)) {
                baseEntryTimeRef.current.set(entryKey, now);
              }
              if (!insideBase && baseEntryTimeRef.current.has(entryKey)) {
                baseEntryTimeRef.current.delete(entryKey);
              }

              lastSuctionStateRef.current.set(cardId, {
                isClosest,
                distanceLevel,
                isFading,
                isHidden: shouldHide,
              });
            } else if (!isActiveCard) {
              if (baseEntryTimeRef.current.has(cardId)) {
                baseEntryTimeRef.current.delete(cardId);
              }
              lastSuctionStateRef.current.set(cardId, {
                isClosest: false,
                distanceLevel: null,
                isFading: false,
                isHidden: false,
              });
            }
          });
        }
      }

      animationFrameId = requestAnimationFrame(updateSuctionEffect);
    };

    animationFrameId = requestAnimationFrame(updateSuctionEffect);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [hoveredCardId, isVisible]); // 🚀 PERFORMANCE FIX: האנימציה מתחילה מחדש כשהתנאים משתנים
  
  // ניקוי cache כשהחלון משתנה גודל
  useEffect(() => {
    const handleResize = () => {
      cachedMachinePositionRef.current = null;
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (allTestimonials.length === 0) {
    return (
      <section id="testimonials" className="py-12 md:py-20 bg-black">
        <div className="container mx-auto px-4 text-center">
          <p style={{ color: colors.textGold.base }}>אין המלצות להצגה</p>
        </div>
      </section>
    );
  }

  return (
    <section 
      ref={sectionRef}
      id="testimonials" 
      className="relative py-4 md:py-6"
      style={{ 
        background: `linear-gradient(180deg, #000000 0%, #1a1a1a 50%, #000000 100%)`,
        paddingTop: '0',
        paddingBottom: 'clamp(0px, 1.5vw, 16px)',
        overflow: 'hidden',
        overflowX: 'hidden',
        overflowY: 'hidden',
        width: '100%',
        maxWidth: '100%',
      }}
    >
      <div className="container mx-auto px-4" style={{ maxWidth: layout.contentMaxWidth }}>
        {/* White background wrapper */}
        <div style={{ 
          backgroundColor: '#FFFFFF', 
          marginLeft: '-1rem', 
          marginRight: '-1rem', 
          width: 'calc(100% + 2rem)', 
          padding: 'clamp(40px, 4vw, 70px) 1rem clamp(40px, 4vw, 70px)' // ✅ הגדלת רווח פנימי: מובייל 40px → דסקטופ 70px (מלמעלה ומלמטה)
        }}>
          {/* Title */}
          <h2 
            className="text-center font-black"
            style={{ 
              fontSize: 'clamp(1.2rem, 4vw, 2.5rem)',
              fontFamily: typography.heading.fontFamily,
              letterSpacing: typography.heading.letterSpacing,
              background: colors.accentSpecial.textGradientRich,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              // marginBottom: 'clamp(2px, 0.5vw, 8px)', // 🔄 הקטנת הרווח בין הכותרת לכרטיסים
               marginBottom: 'clamp(20px, 3vw, 40px)', 
              marginTop: '0',
              paddingTop: '0',
              paddingBottom: '0',
            }}
          >
            הלקוחות שלנו מסתפרים ומספרים
          </h2>


          {/* Layout Container - קרוסלה עם תמונת קליפר בצד ימין */}
          {/* machine-container - עוטף את המכונה והכרטיסים לשליטה ב-z-index */}
          <div 
            className="relative machine-container"
            style={{
              marginBottom: '0px',
              marginTop: '0',
              minHeight: '0px',
              overflow: 'visible',
              position: 'relative',
            }}
          >
          {/* Swiper Carousel - עיצוב חדש ומרשים */}
          <div 
            className="relative testimonials-swiper-container"
            style={{ 
              minHeight: window.innerWidth > 1024 ? '300px' : '0px',
              paddingBottom: '0px', 
              marginTop: window.innerWidth > 1024 ? '0' : 'clamp(-10px, -2vw, -5px)', // 🔄 הסרת הרווח הלבן בדסקטופ
              marginBottom: window.innerWidth > 1024 ? '0px' : 'clamp(10px, 1.5vw, 30px)',
              marginLeft: window.innerWidth > 1024 ? '-50px' : '0',
              position: 'relative',
            }}
          >
          <Swiper
            modules={[Autoplay, Mousewheel]}
            spaceBetween={12}
            slidesPerView={3}
            slidesPerGroup={1}
            watchSlidesProgress={true}
            centeredSlides={true}
            
            breakpoints={{
              320: {
                slidesPerView: 1,
                slidesPerGroup: 1,
                spaceBetween: 0,
                centeredSlides: true,
              },
              431: {
                slidesPerView: 1,
                slidesPerGroup: 1,
                spaceBetween: 0,
                centeredSlides: true,
              },
              640: {
                slidesPerView: 2,
                slidesPerGroup: 1,
                spaceBetween: 15,
                centeredSlides: false,
              },
              1024: {
                slidesPerView: 2,
                slidesPerGroup: 1,
                spaceBetween: 12,
                centeredSlides: false,
              },
              1400: {
                slidesPerView: 3,
                slidesPerGroup: 1,
                spaceBetween: 12,
                centeredSlides: false,
              },
            }}

            autoplay={{
              // delay: window.innerWidth <= 1024 ? 2000 : 500
              delay: window.innerWidth <= 1024 ? 2000 : 400 // שנה מ-500 ל-1500

              , // 🔄 האטת הקרוסלה בדסקטופ - delay - כמה זמן כל כרטיס נשאר לפני שעובר לכרטיס הבא
              disableOnInteraction: false,
              pauseOnMouseEnter: window.innerWidth > 1024,
              stopOnLastSlide: false,
            }}
            // speed={600}//speed - כמה זמן לוקח המעבר בין כרטיס לכרטיס
            speed={1000}  // שנה מ-600 ל-1000
            loop={allTestimonials.length >= 3}
            grabCursor={true}
            allowTouchMove={true}
            simulateTouch={true}
            mousewheel={{
              enabled: true,
              forceToAxis: true,
              sensitivity: 1,
            }}
            onSwiper={(swiper: SwiperType) => {
              swiperRef.current = swiper;
              setActiveIndex(swiper.realIndex || 0);
              previousIndexRef.current = swiper.realIndex || 0;
            }}
            onSlideChange={(swiper: SwiperType) => {
              const currentRealIndex = swiper.realIndex || 0;
              
              // עדכון אינדקס הכרטיס הפעיל
              setActiveIndex(currentRealIndex);
              previousIndexRef.current = currentRealIndex;
            }}
            style={{ 
              width: '100%',
              paddingBottom: '0',
              marginBottom: '0',
            }}
          >
            {allTestimonials.map((testimonial) => {
              return (
              <SwiperSlide 
                key={testimonial.id}
                style={{
                  display: 'flex',
                  justifyContent: 'center', // ממרכז את הכרטיס
                  alignItems: 'center',
                  padding: '0',
                  margin: '0',
                }}
              >
                <div 
                  ref={(el) => {
                    if (el) {
                      cardRefs.current.set(testimonial.id, el);
                    } else {
                      cardRefs.current.delete(testimonial.id);
                    }
                  }}
                  data-card-id={testimonial.id}
                  className="rounded-2xl flex flex-col h-full testimonial-card-modern"
                  style={{
                    backgroundColor: '#000000',
                    border: `clamp(3px, 0.5vw, 5px) solid #F5B658`, // מובייל: 3px, דסקטופ: 5px
                    borderRadius: '44px',
                    boxShadow: `6px 6px 13.6px rgba(245, 182, 88, 0.22)`,
                    width: 'clamp(290px, 34vw, 380px)', // ✅ רוחב רחב עוד יותר: מובייל 290px → דסקטופ 380px
                    height: 'clamp(180px, 22vw, 240px)', // ✅ גובה קצר עוד יותר: מובייל 180px → דסקטופ 240px
                    minHeight: 'clamp(180px, 22vw, 240px)', // ✅ מינימום זהה לגובה
                    maxWidth: '92%',
                    margin: '0', // ללא margin - המרכוז ב-SwiperSlide
                    transition: 'border 0.3s ease, box-shadow 0.3s ease',
                    padding: 'clamp(0.875rem, 2.5vw, 1.75rem) clamp(0.75rem, 2vw, 1.5rem)', // מובייל: 14px 12px, דסקטופ: 28px 24px
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    zIndex: 500,
                  }}
                  onMouseEnter={(e) => {
                    setHoveredCardId(testimonial.id);
                    e.currentTarget.style.boxShadow = `0px 20px 50px rgba(245, 182, 88, 0.5),
                                                        0px 0px 30px rgba(245, 182, 88, 0.3),
                                                        0px 0px 15px rgba(245, 182, 88, 0.2)`;
                    e.currentTarget.style.borderColor = '#F5B658';
                    e.currentTarget.style.borderWidth = '5px';
                  }}
                  onMouseLeave={(e) => {
                    setHoveredCardId(null);
                    e.currentTarget.style.boxShadow = `6px 6px 13.6px rgba(245, 182, 88, 0.22)`;
                    e.currentTarget.style.borderColor = '#F5B658';
                    e.currentTarget.style.borderWidth = '5px';
                  }}
                >
                  <div className="testimonial-suction-trail" />

                  <div style={{ position: 'relative', zIndex: 10, padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                    {/* Text - מרכזי וגדול יותר */}
                    <p 
                      className="leading-relaxed"
                      style={{
                        fontSize: 'clamp(1rem, 1.5vw, 1.2rem)',
                        color: '#FFFFFF',
                        lineHeight: '1.25', // 🔄 שונה מ-1.4 ל-1.25 - שורות קרובות יותר לרספונסיביות טובה יותר
                        wordBreak: 'break-word', // 🔄 מילים ארוכות יתחלקו בין שורות
                        overflowWrap: 'break-word', // 🔄 מילים יעברו שורה בצורה חכמה
                        hyphens: 'auto', // 🔄 מקפים אוטומטיים
                        flex: 1,
                        marginTop: '0',
                        marginBottom: '0.4rem',
                        padding: '0',
                        textAlign: 'center',
                      }}
                    >
                      {testimonial.text.map((line, index) => (
                        <React.Fragment key={index}>
                          {line}
                          {index < testimonial.text.length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </p>

                    {/* Name - במרכז למטה */}
                    <p 
                      className="font-black"
                      style={{ 
                        fontSize: 'clamp(0.9rem, 1.5vw, 1.2rem)',
                        color: '#F5B658',
                        marginBottom: '0',
                        marginTop: '0',
                        padding: '0',
                        lineHeight: '1.2',
                        textAlign: 'center',
                      }}
                    >
                      {testimonial.name}
                    </p>
                  </div>
                </div>
              </SwiperSlide>
              );
            })}
          </Swiper>
          </div>

          {/* שכבה תחתונה - גוף המכונה (כרטיסים צריכים להיעלם עליו) */}
          <div 
            ref={clipperBodyRef}
            className="clipper-head-container clipper-head-base-layer"
            style={{
              top: 'calc(50% + 30px)', // הורדתי ב-3 ס"מ למטה
            }}
          >
            <img 
              ref={clipperBaseImageRef}
              src={clipperHeadBase}
              alt=""
              aria-hidden="true"
              width="158"
              height="271"
              loading="lazy"
              className="clipper-head-base"
            />
          </div>

          {/* שכבה עליונה - ראש המכונה (כרטיסים צריכים להיראות עליו) */}
          <div 
            ref={clipperContainerRef}
            className="clipper-head-container clipper-head-blades-layer"
            style={{
              top: 'calc(50% + 30px)', // אותו מיקום בדיוק
            }}
          >
            <img 
              ref={clipperBladesImageRef}
              src={clipperHeadBlades}
              alt=""
              aria-hidden="true"
              width="82"
              height="224"
              loading="lazy"
              className="clipper-head-blades"
            />
          </div>
        </div>
        </div>


        {/* Product Card - כרטיס מוצר עם הכפתור בתוכו */}
        <ProductCard />
      </div>
    </section>
  );
};

export default TestimonialsSection;
